import React from "react";

import { map, some } from "lodash/fp";

import { FormControlLabel, Typography } from "@mui/material";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import type { ReportView } from "@app/analytics/MeetingReports/types";
import { PRODUCTIVITY_PROVIDERS } from "@app/common/constants/providers";
import { formattedListWithConjunction } from "@app/common/utils";

import Checkbox from "@rui/Checkbox";
import { Link } from "@rui/Link";
import TooltipIcon from "@rui/TooltipIcon";

import { MenuItemContainer } from "./styles";

const REPORT_VIEW_OPTIONS: { label: string; value: ReportView }[] = [
  {
    label: "Meetings",
    value: "session",
  },
  {
    label: "Email / Msg",
    value: "topic",
  },
];

const TopicReportTooltipTitle = ({ connected }: { connected: boolean }) => {
  const providerList = formattedListWithConjunction(
    map("displayNameShort", PRODUCTIVITY_PROVIDERS)
  );
  const integrationRoute = PRODUCTIVITY_PROVIDERS[0].integrationUrl;

  if (connected) {
    return (
      <>
        Email and Messaging reports are generated when new topics are detected
        across your {providerList}{" "}
        <Link underline to={integrationRoute} color="shades.secondary.200">
          integrations
        </Link>
        .
      </>
    );
  }

  return (
    <>
      <Link underline to={integrationRoute} color="shades.secondary.200">
        Connect integrations
      </Link>{" "}
      to get started with Email and Messaging reports, which are generated when
      new topics are detected across {providerList}.
    </>
  );
};

const ReportViewSelector = () => {
  const { isProviderConnected } = useIntegrations();

  const hasConnectedProductivityIntegration = some(
    ({ id }) => isProviderConnected({ providerId: id }),
    PRODUCTIVITY_PROVIDERS
  );

  const [reportViews, toggleReportView] = useReportsTable((state) => [
    state.reportViews,
    state.toggleReportView,
  ]);

  const handleToggleReportView = (view: ReportView) => () => {
    toggleReportView(view);
  };

  return (
    <MenuItemContainer spacing={1}>
      {REPORT_VIEW_OPTIONS.map(({ label, value }) => {
        const checked = reportViews.includes(value);
        return (
          <React.Fragment key={value}>
            <FormControlLabel
              control={<Checkbox variant="filled" />}
              label={
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.600"
                  fontWeight={checked ? 500 : 400}
                >
                  {label}
                </Typography>
              }
              checked={checked}
              onChange={handleToggleReportView(value)}
              sx={{ m: 0, ml: -1 }}
            />
            {value === "topic" && (
              <TooltipIcon
                variant="help"
                title={
                  <TopicReportTooltipTitle
                    connected={hasConnectedProductivityIntegration}
                  />
                }
              />
            )}
          </React.Fragment>
        );
      })}
    </MenuItemContainer>
  );
};

export default ReportViewSelector;
